/**
 * Formatting helpers. Every function copes with null/undefined input and
 * returns an em dash for missing values so pages never render "null".
 */
export const EMPTY = '—';

const toDate = (value) => {
  if (!value) return null;
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by the Date constructor,
  // which can shift them a day in western timezones. Parse them as local dates.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value, options = {}) => {
  const date = toDate(value);
  if (!date) return EMPTY;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', ...options });
};

export const formatDateShort = (value) =>
  formatDate(value, { month: 'short', day: 'numeric', year: 'numeric' });

// Today's date as the YYYY-MM-DD string date inputs expect.
export const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const isBlank = (value) => value === null || value === undefined || value === '';

export const formatNumber = (value, decimals = 1) => {
  if (isBlank(value) || Number.isNaN(Number(value))) return EMPTY;
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

// A number followed by a unit, e.g. "8.1 m". Missing values stay as a dash.
export const formatMeasure = (value, unit, decimals = 1) => {
  if (isBlank(value) || Number.isNaN(Number(value))) return EMPTY;
  return `${formatNumber(value, decimals)}${unit === '%' ? '' : ' '}${unit}`;
};

export const formatPercentage = (value, decimals = 1) => formatMeasure(value, '%', decimals);

export const formatCoordinate = (lat, lon, decimals = 5) => {
  if (isBlank(lat) || isBlank(lon)) return EMPTY;
  return `${Number(lat).toFixed(decimals)}, ${Number(lon).toFixed(decimals)}`;
};

export const pluralize = (count, singular, plural = `${singular}s`) =>
  `${formatNumber(count, 0)} ${Number(count) === 1 ? singular : plural}`;

// Form inputs hold strings; the API wants numbers or null.
export const toNumberOrNull = (value) => {
  if (isBlank(value)) return null;
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

// The API sends decimals as strings ("72.40"); a form shows them as typed ("72.4").
export const toInputValue = (value) => {
  const number = toNumberOrNull(value);
  return number === null ? '' : String(number);
};

export const normalizeNumbers = (values, keys) =>
  keys.reduce((acc, key) => ({ ...acc, [key]: toNumberOrNull(values[key]) }), { ...values });

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length <= maxLength ? text : `${text.substring(0, maxLength).trimEnd()}…`;
};

// Turns a stored slug like "storm_damage" into "Storm damage".
export const humanize = (value) => {
  if (isBlank(value)) return EMPTY;
  const text = String(value).replace(/[_-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};
